import type { DashboardSnapshotV2, PlayerProfile } from "./types";

const asOf = "2026-08-27T18:17:51Z";

const player = (
  id: string,
  name: string,
  team: string,
  positions: string[],
  recommendation: PlayerProfile["recommendation"],
  score: number,
  seasonValue: number,
  weeklyImpact: number,
  extras: Partial<PlayerProfile> = {},
): PlayerProfile => ({
  id,
  name,
  team,
  positions,
  age: extras.age ?? 27,
  recommendation,
  score,
  trend: extras.trend ?? "flat",
  trendLabel: extras.trendLabel ?? "Steady over 14 days",
  seasonValue,
  weeklyImpact,
  confidence: extras.confidence ?? "medium",
  essence: extras.essence ?? "Balanced contributor with a stable everyday role.",
  outlook: extras.outlook ?? "Skills and playing time point to reliable rest-of-season value.",
  suggestion: extras.suggestion ?? "Use when the category fit is clear.",
  risk: extras.risk ?? "Normal performance volatility.",
  trigger: extras.trigger ?? "Recheck after the next lineup or role update.",
  categories: extras.categories ?? ["R", "HR"],
  sourceAsOf: asOf,
  analysisStatus: extras.analysisStatus ?? "ready",
  keyEvidence: extras.keyEvidence ?? [{ id: `${id}-role`, label: "Role", value: extras.availability ?? "Accepted roster evidence", asOf }],
  whatChanged: extras.whatChanged ?? [],
  history: extras.history ?? [{ asOf, score, seasonValue, weeklyImpact, note: extras.trendLabel ?? "Current accepted player evaluation" }],
  ...extras,
});

const roster = [
  player("p1", "Bobby Witt Jr.", "KC", ["SS"], "start", 96, 94, 83, {
    age: 26,
    trend: "up",
    trendLabel: "+12% hard-hit rate over 14 days",
    essence: "Five-category anchor with elite volume and speed.",
    outlook: "Prime-age star whose role, contact and power give him one of the safest fantasy floors.",
    suggestion: "Lock him in; build the rest of the lineup around his balanced production.",
    risk: "Only schedule volume can materially reduce his weekly ceiling.",
    trigger: "No action unless an injury changes his availability.",
    categories: ["R", "HR", "RBI", "SB", "AVG"],
    isMine: true,
    cutRisk: 1,
  }),
  player("p2", "Kyle Tucker", "CHC", ["OF"], "start", 92, 90, 79, {
    age: 29,
    trend: "up",
    trendLabel: "Power and steals both trending up",
    essence: "Middle-order power with uncommon stolen-base upside.",
    outlook: "Premium plate discipline supports a strong floor even when the hits cool.",
    suggestion: "Start in every matchup.",
    categories: ["R", "HR", "RBI", "SB"],
    isMine: true,
    cutRisk: 1,
  }),
  player("p3", "José Ramírez", "CLE", ["3B"], "start", 91, 91, 76, {
    age: 33,
    trendLabel: "Stable elite production",
    essence: "Bankable switch-hitting run producer who rarely leaves the lineup.",
    suggestion: "Automatic start at a scarce position.",
    categories: ["R", "HR", "RBI", "SB"],
    isMine: true,
    cutRisk: 1,
  }),
  player("p4", "Corbin Carroll", "ARI", ["OF"], "start", 88, 86, 81, {
    age: 26,
    trend: "up",
    trendLabel: "Three steals in the last week",
    essence: "Speed-first leadoff catalyst with enough power to swing a matchup.",
    suggestion: "Start; his speed directly attacks the closest category.",
    categories: ["R", "SB"],
    isMine: true,
    cutRisk: 2,
  }),
  player("p5", "Logan Webb", "SF", ["SP"], "start", 84, 82, 75, {
    age: 29,
    trendLabel: "Pitch count and command are stable",
    essence: "Durable innings source with ratio control and a high win chance at home.",
    outlook: "Volume remains the carrying skill; matchup quality determines his weekly ceiling.",
    suggestion: "Start at home; use the matchup view before road starts.",
    categories: ["W", "ERA", "WHIP", "K"],
    isMine: true,
    cutRisk: 2,
    probableStarts: 2,
    inningsOutlook: 12.1,
    strikeoutOutlook: 11,
    ratioRisk: 24,
  }),
  player("p6", "George Kirby", "SEA", ["SP"], "start", 82, 80, 72, {
    age: 28,
    trend: "down",
    trendLabel: "Velocity down 0.6 mph in two starts",
    essence: "Elite control keeps traffic low even when strikeouts fluctuate.",
    suggestion: "Start, but monitor velocity before the second outing.",
    risk: "Recent velocity dip adds moderate ratio risk.",
    categories: ["WHIP", "K", "W"],
    isMine: true,
    cutRisk: 3,
    probableStarts: 2,
    inningsOutlook: 11.2,
    strikeoutOutlook: 10,
    ratioRisk: 32,
  }),
  player("p7", "Ryan Helsley", "NYM", ["RP"], "hold", 73, 72, 56, {
    age: 32,
    trendLabel: "Save chances remain role-dependent",
    essence: "High-velocity closer whose value comes from scarce saves.",
    suggestion: "Hold while he owns the ninth inning.",
    categories: ["SV", "ERA", "WHIP"],
    isMine: true,
    cutRisk: 18,
  }),
  player("p8", "Masyn Winn", "STL", ["SS"], "hold", 59, 61, 49, {
    age: 24,
    trend: "down",
    trendLabel: "Fewer runs as lineup slot slipped",
    essence: "Contact-and-speed middle infielder with modest power.",
    outlook: "Young enough to rebound, but the current lineup slot caps his run ceiling.",
    suggestion: "Hold as middle-infield depth; cut only for a clear two-category upgrade.",
    categories: ["R", "SB", "AVG"],
    isMine: true,
    cutRisk: 48,
  }),
  player("p9", "Nolan Jones", "COL", ["OF"], "cut", 42, 46, 35, {
    age: 28,
    trend: "down",
    trendLabel: "Playing time and contact both falling",
    essence: "Power-speed upside is being erased by strikeouts and unstable playing time.",
    outlook: "The ceiling survives, but the role no longer warrants a protected roster spot.",
    suggestion: "First cut if an everyday hitter with a category fit is available.",
    risk: "A rebound could make the cut painful, but current opportunity cost is higher.",
    trigger: "Cut if his next three starts include another platoon benching.",
    categories: ["HR", "SB"],
    isMine: true,
    cutRisk: 86,
  }),
];

const hitters = [
  player("h1", "Colson Montgomery", "CWS", ["SS", "3B"], "add", 84, 71, 89, {
    age: 24,
    availability: "Free agent",
    trend: "up",
    trendLabel: "Everyday role; five extra-base hits in seven games",
    essence: "Young left-handed power bat with immediate middle-infield eligibility.",
    outlook: "A secured everyday role raises both the floor and the chance of a September breakout.",
    suggestion: "Add for Nolan Jones; he improves HR and RBI without sacrificing a scarce slot.",
    risk: "Rookie contact risk can hurt AVG.",
    trigger: "Act before tonight's first pitch.",
    categories: ["HR", "RBI"],
  }),
  player("h2", "Victor Scott II", "STL", ["OF"], "add", 79, 54, 92, {
    age: 25,
    availability: "Free agent",
    trend: "up",
    trendLabel: "Four starts and three steals this week",
    essence: "Pure speed specialist whose everyday role creates category-winning upside.",
    outlook: "His fantasy value rises sharply whenever he starts and reaches base.",
    suggestion: "Add only if stolen bases remain the closest category.",
    risk: "Batting-average floor and role security are below average.",
    trigger: "Add if he starts again Friday; otherwise watch.",
    categories: ["SB", "R"],
  }),
  player("h3", "Coby Mayo", "BAL", ["1B", "3B"], "watch", 74, 68, 78, {
    age: 24,
    availability: "Free agent",
    trend: "up",
    trendLabel: "Hard contact up; lineup spot not yet secure",
    essence: "Impact raw power with playing-time uncertainty.",
    suggestion: "Watch for consecutive starts before using a roster move.",
    risk: "Can lose at-bats against right-handed pitching.",
    categories: ["HR", "RBI"],
  }),
  player("h4", "Tyler Freeman", "COL", ["2B", "3B", "OF"], "stream", 68, 58, 73, {
    age: 27,
    availability: "Free agent",
    trendLabel: "Seven-game home stand begins Monday",
    essence: "Multi-position contact bat whose schedule creates short-term value.",
    suggestion: "Stream for volume, then reassess after the home stand.",
    categories: ["R", "AVG"],
  }),
  player("h5", "Will Benson", "CIN", ["OF"], "watch", 63, 57, 66, {
    age: 28,
    availability: "Free agent",
    trend: "down",
    trendLabel: "Power intact; strikeouts rising",
    essence: "Power-speed lottery ticket best used against right-handed pitching.",
    suggestion: "Watch unless you can manage daily platoons.",
    categories: ["HR", "SB"],
  }),
];

const pitchers = [
  player("sp1", "Zebby Matthews", "MIN", ["SP"], "stream", 88, 64, 93, {
    age: 26,
    availability: "Free agent",
    trend: "up",
    trendLabel: "Two-start week with both games at home",
    essence: "Command-forward starter with a favorable two-start runway.",
    outlook: "The skill floor is ordinary, but schedule and workload combine for elite weekly volume.",
    suggestion: "Best two-start stream: expected to add 11–13 innings and 10–12 strikeouts.",
    risk: "Second start carries moderate ERA risk.",
    trigger: "Add before Monday lock if both starts remain probable.",
    categories: ["W", "K"],
    probableStarts: 2,
    inningsOutlook: 11.8,
    strikeoutOutlook: 11,
    ratioRisk: 36,
  }),
  player("sp2", "Hurston Waldrep", "ATL", ["SP"], "stream", 81, 61, 85, {
    age: 24,
    availability: "Free agent",
    trend: "up",
    trendLabel: "Whiff rate up in three outings",
    essence: "Strikeout-upside arm whose control remains the weekly swing factor.",
    suggestion: "Use for strikeouts if WHIP has a cushion.",
    risk: "Walks can erase the volume advantage.",
    categories: ["K", "W"],
    probableStarts: 2,
    inningsOutlook: 10.4,
    strikeoutOutlook: 12,
    ratioRisk: 53,
  }),
  player("sp3", "Cade Horton", "CHC", ["SP"], "add", 78, 69, 76, {
    age: 25,
    availability: "Free agent",
    trendLabel: "One high-quality start, strong win odds",
    essence: "Efficient young starter with balanced ratios and strikeouts.",
    suggestion: "Add for rest-of-season value, not just this week.",
    categories: ["W", "ERA", "WHIP", "K"],
    probableStarts: 1,
    inningsOutlook: 6.1,
    strikeoutOutlook: 7,
    ratioRisk: 28,
  }),
  player("sp4", "Ben Brown", "CHC", ["SP", "RP"], "watch", 70, 60, 72, {
    age: 26,
    availability: "Free agent",
    trend: "down",
    trendLabel: "Second start is not confirmed",
    essence: "Bat-missing swingman whose fantasy value depends on starter volume.",
    suggestion: "Watch; do not pay for an unconfirmed second start.",
    categories: ["K"],
    probableStarts: 1,
    inningsOutlook: 5.2,
    strikeoutOutlook: 6,
    ratioRisk: 49,
  }),
];

export const fallbackSnapshot: DashboardSnapshotV2 = {
  meta: {
    schemaVersion: "dashboard_snapshot.v2",
    snapshotId: "fixture-2026-08-27",
    generatedAt: asOf,
    leagueName: "The League",
    teamName: "Tim's Team",
    opponentName: "Double Baggers",
    weekLabel: "Week 22 · Thu–Sun",
    dataQuality: "verified",
    freshness: [
      { label: "Yahoo league", asOf, status: "fresh" },
      { label: "MLB schedule", asOf: "2026-08-27T17:56:00Z", status: "fresh" },
      { label: "Player news", asOf: "2026-08-27T16:45:00Z", status: "fresh" },
    ],
    analysisStatus: "ready",
    keyEvidence: [
      { id: "fixture-matchup", label: "Matchup model", value: "64% win probability", asOf, confidence: "high" },
      { id: "fixture-roster", label: "Roster reconciliation", value: "Complete", asOf, confidence: "high" },
    ],
    whatChanged: [{ id: "fixture-change", title: "Speed category moved into range", detail: "The stolen-base category is now within one event.", direction: "up", happenedAt: asOf, materiality: "medium" }],
    history: [{ asOf: "2026-08-27T12:00:00Z", metrics: { matchup_win_probability: .59 }, note: "Morning accepted snapshot" }, { asOf, metrics: { matchup_win_probability: .64 }, note: "Current accepted snapshot" }],
    analysisUnknowns: ["Friday batting orders are not confirmed."],
    evidenceGates: [{ id: "gate-privacy", label: "Privacy gate", value: "Yes" }, { id: "gate-roster", label: "Roster gate", value: "Yes" }],
    analysisAcceptedOnly: true,
    historyAcceptedOnly: true,
  },
  today: {
    headline: "Protect the ratios. Buy only the speed you need.",
    deck: "You lead six categories, four remain live, and one low-risk roster move creates the clearest edge.",
    brief: [
      "Start Logan Webb for two home outings; his projected innings help wins and strikeouts without taking an unnecessary ratio gamble.",
      "Stolen bases are the closest hitter category. Victor Scott II becomes useful only if he is confirmed in Friday's lineup.",
      "Nolan Jones is the first cut: falling playing time makes his upside less useful than Colson Montgomery's everyday power role.",
    ],
    actions: [
      { id: "a1", priority: 1, label: "Best move", player: "Colson Montgomery", decision: "Add for Nolan Jones", why: "Montgomery has the everyday role and improves the two categories where one swing matters most.", impact: "+0.7 expected category wins", deadline: "Before first pitch tonight", confidence: "high", factIds: ["role:h1", "matchup:hr", "matchup:rbi"] },
      { id: "a2", priority: 2, label: "Pitching call", player: "Logan Webb", decision: "Start both outings", why: "Two home starts add useful volume while the model keeps ERA risk below the team threshold.", impact: "+11 K · +0.5 win probability", deadline: "Before Monday lineup lock", confidence: "high", factIds: ["starts:p5", "matchup:k"] },
      { id: "a3", priority: 3, label: "Conditional", player: "Victor Scott II", decision: "Add only if starting Friday", why: "One steal can flip the category, but a bench role produces no advantage.", impact: "+9% chance to win SB", deadline: "When Friday lineups post", confidence: "medium", factIds: ["lineup:h2", "matchup:sb"] },
    ],
    changes: [
      { id: "c1", title: "Scott moved into a starting role", detail: "He started four straight games and stole three bases.", direction: "up", happenedAt: "2h ago" },
      { id: "c2", title: "Kirby's velocity needs watching", detail: "Four-seam velocity is down 0.6 mph across two starts.", direction: "down", happenedAt: "4h ago" },
      { id: "c3", title: "No league rival transaction changed the plan", detail: "Double Baggers did not add a closer or speed specialist.", direction: "flat", happenedAt: "6h ago" },
    ],
    watchList: [hitters[1], pitchers[1], roster[5]],
    keyEvidence: [
      { id: "today-e1", label: "Closest category", value: "Stolen bases: one behind", asOf, confidence: "high" },
      { id: "today-e2", label: "Best roster edge", value: "+0.7 expected category wins", asOf, confidence: "high" },
    ],
    materiality: { isMaterial: true, level: "medium", reason: "A confirmed everyday role created a roster move that now clears the decision gate.", changedAt: asOf },
    stories: [{
      id: "fixture-story-1", section: "The Lead", headline: "One everyday role changes the move", summary: "The best available hitter now clears the roster cut line and attacks two live categories.", narrative: "The useful change is opportunity, not one hot box score. Everyday at-bats raise both this week's ceiling and the chance the move keeps paying in September.", whyItMatters: "The move no longer depends on a one-category gamble.", actionTrigger: "Act before tonight's first pitch if the role remains confirmed.", confidence: "high", sources: [{ id: "fixture-source-yahoo", label: "Accepted Yahoo snapshot", value: "Roster and availability", asOf }, { id: "fixture-source-mlb", label: "MLB lineup evidence", value: "Everyday role", asOf }], materiality: { score: 72, tier: "Medium", decisionImpact: "The recommended hold becomes an add/drop candidate.", newness: "New since the morning snapshot" }, change: { status: "changed", summary: "Role evidence moved from uncertain to everyday.", previousAsOf: "2026-08-27T12:00:00Z" },
    }],
  },
  myTeam: {
    summary: "The core is strong. The only clear weakness is an outfield bench spot that is no longer earning everyday at-bats.",
    roster,
    cutLine: 50,
    coverage: [
      { position: "C", actual: 1, target: 1, change: 0, note: "On target" },
      { position: "1B", actual: 2, target: 2, change: 0, note: "On target" },
      { position: "2B", actual: 1, target: 2, change: -1, note: "One flexible bat short" },
      { position: "SS", actual: 3, target: 2, change: 1, note: "One spot of surplus" },
      { position: "3B", actual: 1, target: 1, change: 0, note: "Elite anchor" },
      { position: "OF", actual: 4, target: 5, change: -1, note: "Needs one everyday role" },
      { position: "SP", actual: 6, target: 6, change: 0, note: "Balanced" },
      { position: "RP", actual: 3, target: 3, change: 0, note: "Enough save coverage" },
    ],
  },
  matchup: {
    overallWinProbability: 64,
    projectedScore: "6.3–4.7",
    recommendation: "Defend ERA and WHIP; attack steals and strikeouts with targeted volume.",
    categories: [
      { category: "Runs", mine: 31, theirs: 27, unit: "R", winProbability: 66, swing: 0.2, status: "lead", action: "Hold the everyday hitters." },
      { category: "Home runs", mine: 8, theirs: 7, unit: "HR", winProbability: 58, swing: 0.4, status: "toss-up", action: "Montgomery adds useful power." },
      { category: "RBI", mine: 29, theirs: 28, unit: "RBI", winProbability: 54, swing: 0.3, status: "toss-up", action: "Prioritize middle-order at-bats." },
      { category: "Stolen bases", mine: 4, theirs: 5, unit: "SB", winProbability: 43, swing: 0.7, status: "trail", action: "Scott is worth a move only if starting." },
      { category: "Average", mine: 0.278, theirs: 0.264, unit: "AVG", winProbability: 72, swing: 0.1, status: "lead", action: "Avoid empty speed specialists." },
      { category: "Wins", mine: 5, theirs: 4, unit: "W", winProbability: 61, swing: 0.4, status: "lead", action: "Use Webb's two starts." },
      { category: "Saves", mine: 3, theirs: 2, unit: "SV", winProbability: 69, swing: 0.2, status: "lead", action: "Keep Helsley active." },
      { category: "Strikeouts", mine: 48, theirs: 53, unit: "K", winProbability: 45, swing: 0.7, status: "trail", action: "Add one controlled two-start pitcher." },
      { category: "ERA", mine: 3.42, theirs: 3.89, unit: "ERA", winProbability: 65, swing: 0.5, status: "lead", action: "Do not chase weak road starts." },
      { category: "WHIP", mine: 1.18, theirs: 1.24, unit: "WHIP", winProbability: 63, swing: 0.5, status: "lead", action: "Prefer command over raw strikeouts." },
    ],
    scenarios: [
      { id: "balanced", label: "Balanced", description: "Montgomery in, Webb active, no extra ratio risk.", winProbability: 64, categoryWins: 6, risk: "Low", deltas: { "Home runs": 3, RBI: 2, Strikeouts: 3 } },
      { id: "chase-speed", label: "Chase speed", description: "Add Scott if he starts and push the stolen-base toss-up.", winProbability: 67, categoryWins: 7, risk: "Medium", deltas: { "Stolen bases": 12, Average: -3 } },
      { id: "chase-volume", label: "Chase pitching", description: "Add Matthews for two starts and accept more ratio variance.", winProbability: 66, categoryWins: 7, risk: "Medium", deltas: { Strikeouts: 13, Wins: 8, ERA: -5, WHIP: -4 } },
    ],
  },
  league: {
    playoffLine: 6,
    summary: "You sit fourth with an 82% playoff chance. A two-week cushion separates you from the seventh-place team.",
    standings: [
      { rank: 1, team: "Launch Angle", wins: 136, losses: 92, ties: 12, winPct: 0.592, gamesBack: 0, playoffProbability: 99, power: 94, pickupsLeft: 2, pickupsBasis: "Weekly limit minus accepted moves used", pickupsConfidence: "high" },
      { rank: 2, team: "Southside Sluggers", wins: 130, losses: 98, ties: 12, winPct: 0.567, gamesBack: 6, playoffProbability: 97, power: 89, pickupsLeft: 1 },
      { rank: 3, team: "Double Baggers", wins: 124, losses: 104, ties: 12, winPct: 0.542, gamesBack: 12, playoffProbability: 91, power: 84, pickupsLeft: 2, isRival: true },
      { rank: 4, team: "Tim's Team", wins: 119, losses: 109, ties: 12, winPct: 0.521, gamesBack: 17, playoffProbability: 82, power: 81, pickupsLeft: 3, pickupsBasis: "Direct accepted league evidence", pickupsConfidence: "high", isMine: true },
      { rank: 5, team: "Bat Flips", wins: 115, losses: 113, ties: 12, winPct: 0.504, gamesBack: 21, playoffProbability: 69, power: 75, pickupsLeft: 0 },
      { rank: 6, team: "Warning Track", wins: 112, losses: 116, ties: 12, winPct: 0.492, gamesBack: 24, playoffProbability: 56, power: 72, pickupsLeft: 1 },
      { rank: 7, team: "The Mendoza Line", wins: 108, losses: 120, ties: 12, winPct: 0.475, gamesBack: 28, playoffProbability: 38, power: 68, pickupsLeft: 2 },
      { rank: 8, team: "Unwritten Rules", wins: 101, losses: 127, ties: 12, winPct: 0.446, gamesBack: 35, playoffProbability: 14, power: 61, pickupsLeft: 3 },
      { rank: 9, team: "Baltimore Chops", wins: 96, losses: 132, ties: 12, winPct: 0.425, gamesBack: 40, playoffProbability: 4, power: 56, pickupsLeft: 1 },
      { rank: 10, team: "Ghost Runners", wins: 90, losses: 138, ties: 12, winPct: 0.4, gamesBack: 46, playoffProbability: 1, power: 50, pickupsLeft: 2 },
    ],
  },
  hitters: { summary: "Montgomery is the best broad upgrade; Scott is the best one-category weapon. Names are labeled so every dot is identifiable.", players: hitters },
  pitchers: { summary: "Matthews offers the safest two-start volume. Waldrep has more strikeout upside but materially more WHIP risk.", players: pitchers },
  transactions: {
    summary: "Two recent drops deserve attention. Reasons labeled as inference are model interpretations, not reports from managers.",
    rows: [
      { id: "t1", happenedAt: "Today 1:42 PM", fantasyTeam: "Warning Track", player: "Coby Mayo", mlbTeam: "BAL", type: "drop", worthwhile: true, value: 74, reason: "Likely clearing a spot for immediate pitching volume.", reasonKind: "league inference", fitForMe: "Power watch; not better than Montgomery today.", waiverTiming: "Clears Sat 2:00 AM", confidence: "medium" },
      { id: "t2", happenedAt: "Today 11:18 AM", fantasyTeam: "Double Baggers", player: "Jeff Hoffman", mlbTeam: "TOR", type: "add", worthwhile: false, value: 45, reason: "Team needed a second save source after a bullpen role change.", reasonKind: "model inference", fitForMe: "No action; saves are already protected.", waiverTiming: "Added", confidence: "medium" },
      { id: "t3", happenedAt: "Yesterday 9:04 PM", fantasyTeam: "Bat Flips", player: "Zebby Matthews", mlbTeam: "MIN", type: "drop", worthwhile: true, value: 88, reason: "Dropped before his two-start schedule became probable.", reasonKind: "league inference", fitForMe: "Best pitching stream if strikeouts remain within five.", waiverTiming: "Clears Fri 9:04 PM", confidence: "high" },
      { id: "t4", happenedAt: "Yesterday 5:22 PM", fantasyTeam: "Launch Angle", player: "Victor Scott II", mlbTeam: "STL", type: "drop", worthwhile: true, value: 79, reason: "Role was uncertain at the time of the move.", reasonKind: "league inference", fitForMe: "Conditional speed add if Friday lineup confirms the role.", waiverTiming: "Clears Fri 5:22 PM", confidence: "medium" },
    ],
  },
  method: {
    summary: "The desk turns a read-only Yahoo league snapshot, MLB schedule and recent performance into category-level decision support. It never makes roster moves.",
    guardrails: ["Yahoo access is read-only.", "Recommendations are model outputs, not guaranteed outcomes.", "Missing or stale sources are shown, never silently imputed.", "Manager motives are labeled as inference unless directly reported."],
    sourceNotes: ["League roster, matchup, standings and transactions: accepted Yahoo snapshot.", "Age, team and schedule: MLB data.", "Trends: bounded recent-game aggregates; no subscriber text is reproduced."],
    calibration: { status: "partial", summary: "Predictions are being collected prospectively; too few outcomes are scored for an accuracy claim.", sampleSize: 18, coverage: 76, observedAccuracy: null, predictionsCollected: 18, outcomesCollected: 6, scoredPredictions: 6, brierScore: .184, noLookaheadGate: true, asOf },
  },
};
